import React, { useState } from 'react';

import { FormattedMessage } from '../../../util/reactIntl';
import { obfuscatedCoordinates } from '../../../util/maps';
import { getDefaultSearchOrigin } from '../../../util/search';
import { distanceInMiles, formatDistanceMiles } from '../../../util/distance';
import { getClassContact } from '../../../util/classContact';
import { DirectionsLink, Map, ProviderContactBlock } from '../../../components';

import css from './ClassDetail.module.css';

/**
 * The location card (right column): address, distance from the parent's saved location, the map
 * (static until interacted with -- same cost-saving pattern as the stock SectionMapMaybe, provider-
 * agnostic Google/Mapbox), and an external "Open in Maps" link. Honors the fuzzy-coordinates config:
 * when obfuscation is on, the external link targets the address text, never the exact point.
 */
const ClassLocationCard = props => {
  const { listing, currentUser, mapsConfig } = props;
  const [isStatic, setIsStatic] = useState(true);

  const { geolocation, publicData = {} } = listing.attributes || {};
  if (!geolocation) {
    return null;
  }

  // The shared contact resolver + block (this content drifted three formats before; every surface
  // renders the same component now).
  const { businessName, address, phone } = getClassContact(listing);
  const origin = getDefaultSearchOrigin(currentUser);
  const distanceLabel = formatDistanceMiles(distanceInMiles(origin, geolocation));

  const cacheKey = listing.id ? `${listing.id.uuid}_${geolocation.lat}_${geolocation.lng}` : null;
  const isFuzzy = mapsConfig.fuzzy.enabled;
  const mapProps = isFuzzy
    ? { obfuscatedCenter: obfuscatedCoordinates(geolocation, mapsConfig.fuzzy.offset, cacheKey) }
    : { address, center: geolocation };
  const map = <Map {...mapProps} useStaticMap={isStatic} />;

  // With fuzzy coordinates the exact point must not leak through the external link.
  const mapsQuery = isFuzzy
    ? address
    : address || `${geolocation.lat},${geolocation.lng}`;

  return (
    <section className={css.card} id="listing-location">
      <h2 className={css.sectionTitle}>
        <FormattedMessage id="ClassDetailPage.locationTitle" />
      </h2>
      <ProviderContactBlock
        className={css.contactBlock}
        businessName={businessName}
        address={address}
        phone={phone}
      />
      {distanceLabel ? (
        <p className={css.locationDistance}>
          <FormattedMessage
            id="ClassDetailPage.milesFromYou"
            values={{ miles: distanceLabel }}
          />
        </p>
      ) : null}
      {isStatic ? (
        <button type="button" className={css.locationMap} onClick={() => setIsStatic(false)}>
          {map}
        </button>
      ) : (
        <div className={css.locationMap}>{map}</div>
      )}
      <DirectionsLink query={mapsQuery} className={css.openInMaps} />
    </section>
  );
};

export default ClassLocationCard;
